/**
 * Motivos de perda — Provider + hook compartilhados (fonte única de verdade).
 */
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { LossReason } from '../../../../types';
import { lossReasonsRepository } from '../../../../infrastructure/repositories/SupabaseLossReasonsRepository';
import { useOrganization } from '../../../../contexts/OrganizationContext';

interface LossReasonsContextData {
    reasons: LossReason[];
    loading: boolean;
    createReason: (reason: Omit<LossReason, 'id' | 'createdAt'>) => Promise<LossReason | undefined>;
    updateReason: (id: string, updates: Partial<LossReason>) => Promise<void>;
    deleteReason: (id: string) => Promise<void>;
    deleteBulk: (ids: string[]) => Promise<void>;
    selectedIds: Set<string>;
    toggleSelection: (id: string) => void;
    toggleAllSelection: (filteredReasons: LossReason[]) => void;
    clearSelection: () => void;
    refetch: () => Promise<void>;
}

const LossReasonsContext = createContext<LossReasonsContextData | undefined>(undefined);

export const LossReasonsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { currentOrganization } = useOrganization();
    const [reasons, setReasons] = useState<LossReason[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    const loadReasons = useCallback(async () => {
        if (!currentOrganization?.id) return;

        setLoading(true);
        try {
            const data = await lossReasonsRepository.findAll(currentOrganization.id);
            setReasons(data);
        } catch (error) {
            console.error('[useLossReasons] Erro ao buscar motivos de perda:', error);
        } finally {
            setLoading(false);
        }
    }, [currentOrganization?.id]);

    useEffect(() => {
        if (currentOrganization?.id) {
            loadReasons();
        }
    }, [currentOrganization?.id, loadReasons]);

    const createReason = useCallback(async (reason: Omit<LossReason, 'id' | 'createdAt'>): Promise<LossReason | undefined> => {
        if (!currentOrganization?.id) return;

        const newReason = await lossReasonsRepository.create(reason, currentOrganization.id);
        setReasons(prev => [newReason, ...prev]);
        return newReason;
    }, [currentOrganization?.id]);

    const updateReason = useCallback(async (id: string, updates: Partial<LossReason>) => {
        if (!currentOrganization?.id) return;

        const updatedReason = await lossReasonsRepository.update(id, updates, currentOrganization.id);
        setReasons(prev => prev.map(r => (r.id === id ? updatedReason : r)));
    }, [currentOrganization?.id]);

    const deleteReason = useCallback(async (id: string) => {
        if (!currentOrganization?.id) return;

        setReasons(prev => prev.filter(r => r.id !== id));
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });

        try {
            await lossReasonsRepository.delete(id, currentOrganization.id);
        } catch (error) {
            console.error('[useLossReasons] Erro ao deletar motivo de perda:', error);
            await loadReasons();
            throw error;
        }
    }, [currentOrganization?.id, loadReasons]);

    const deleteBulk = useCallback(async (ids: string[]) => {
        if (!currentOrganization?.id || ids.length === 0) return;

        setReasons(prev => prev.filter(r => !ids.includes(r.id)));
        setSelectedIds(new Set());

        try {
            await lossReasonsRepository.deleteBulk(ids, currentOrganization.id);
        } catch (error) {
            console.error('[useLossReasons] Erro ao deletar motivos de perda em massa:', error);
            await loadReasons();
            throw error;
        }
    }, [currentOrganization?.id, loadReasons]);

    const toggleSelection = useCallback((id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const toggleAllSelection = useCallback((filteredReasons: LossReason[]) => {
        setSelectedIds(prev => {
            if (prev.size === filteredReasons.length && filteredReasons.length > 0) {
                return new Set();
            }
            return new Set(filteredReasons.map(r => r.id));
        });
    }, []);

    const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

    const value: LossReasonsContextData = {
        reasons,
        loading,
        createReason,
        updateReason,
        deleteReason,
        deleteBulk,
        selectedIds,
        toggleSelection,
        toggleAllSelection,
        clearSelection,
        refetch: loadReasons,
    };

    return React.createElement(LossReasonsContext.Provider, { value }, children);
};

export const useLossReasons = (): LossReasonsContextData => {
    const context = useContext(LossReasonsContext);
    if (!context) {
        throw new Error('useLossReasons must be used within LossReasonsProvider');
    }
    return context;
};
